import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  ReactFlow,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  Handle,
  Position,
  getConnectedEdges,
  useReactFlow,
  MarkerType,
  ConnectionLineType,
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';
import { getAIMappedCase } from '../../services/aiService';
import { 
  Button, 
  Textarea, 
  Chip, 
  Modal, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  useDisclosure, 
  Input 
} from '@heroui/react';

/**
 * CaseMapper - A ReactFlow-based visual case mapping tool for detective work
 * 
 * This component provides an interactive interface for creating and managing 
 * case diagrams using ReactFlow. It supports:
 * - Custom node types for different case elements (suspects, evidence, locations, etc.)
 * - AI-powered case mapping from text reports
 * - Interactive node editing and relationship management
 * - Auto-layout functionality
 * - Undo/redo functionality
 * - Note-taking capabilities
 * - Quick node creation with hover dropdown
 * - Quick label editing with single click
 * 
 * @example
 * <CaseMapper />
 */

// Constants for ReactFlow configuration
const FLOW_KEY = 'case-flow';
const SNAP_GRID = [20, 20];
const DEFAULT_VIEWPORT = { x: 0, y: 0, zoom: 1 };
const MAX_HISTORY = 50;
const HISTORY_SAVE_DELAY = 1000; // ms

// Node ID counter for unique node generation
let nodeIdCounter = 2;

/**
 * Initial nodes configuration for the case flow
 * Follows ReactFlow Node structure with custom data properties
 */
const initialNodes = [
  {
    id: '1',
    type: 'caseNode', // Custom node type
    position: { x: 400, y: 100 },
    data: {
      label: 'Case Start',
      nodeType: 'case',
      color: '#3b82f6',
      size: 'medium'
    },
    style: {
      width: 120,
      height: 80
    }
  }
];

/**
 * Node type styles configuration
 * Maps node types to their visual representation
 */
const NODE_TYPE_STYLES = {
  person: { border: '#6b7280', bg: '#f9fafb', icon: '👤' },
  suspect: { border: '#dc2626', bg: '#fef2f2', icon: '🔍' },
  victim: { border: '#059669', bg: '#f0fdf4', icon: '🛡️' },
  witness: { border: '#7c3aed', bg: '#faf5ff', icon: '👁️' },
  officer: { border: '#1d4ed8', bg: '#eff6ff', icon: '👮' },
  location: { border: '#ea580c', bg: '#fff7ed', icon: '📍' },
  evidence: { border: '#be123c', bg: '#fdf2f8', icon: '🔬' },
  event: { border: '#ca8a04', bg: '#fefce8', icon: '⚡' },
  case: { border: '#6b7280', bg: '#f9fafb', icon: '📋' },
  default: { border: '#6b7280', bg: '#f9fafb', icon: '📄' }
};

/**
 * Color options for node customization
 */
const COLOR_OPTIONS = [
  { value: '#3b82f6', label: 'Blue' },
  { value: '#ef4444', label: 'Red' },
  { value: '#10b981', label: 'Green' },
  { value: '#f59e0b', label: 'Yellow' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#84cc16', label: 'Lime' },
  { value: '#f97316', label: 'Orange' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#6b7280', label: 'Gray' },
];

/**
 * Size options for node scaling
 */
const SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'xlarge', label: 'Extra Large' },
];

/**
 * Text size options for independent text scaling
 */
const TEXT_SIZE_OPTIONS = [
  { value: 'xs', label: 'Extra Small', fontSize: '10px' },
  { value: 'sm', label: 'Small', fontSize: '12px' },
  { value: 'base', label: 'Normal', fontSize: '14px' },
  { value: 'lg', label: 'Large', fontSize: '16px' },
  { value: 'xl', label: 'Extra Large', fontSize: '18px' },
  { value: '2xl', label: 'XXL', fontSize: '20px' },
];

/**
 * Calculate size styles based on size option
 * @param {string} size - Size identifier (small, medium, large, xlarge)
 * @param {string} textSize - Text size identifier (xs, sm, base, lg, xl, 2xl)
 * @returns {Object} Style object with dimensions and typography
 */
const getSizeStyles = (size, textSize = 'base') => {
  const sizeMap = {
    small: { width: '80px', height: '60px', padding: '8px' },
    medium: { width: '120px', height: '80px', padding: '12px' },
    large: { width: '160px', height: '100px', padding: '16px' },
    xlarge: { width: '200px', height: '120px', padding: '20px' }
  };
  
  const textSizeOption = TEXT_SIZE_OPTIONS.find(option => option.value === textSize);
  const fontSize = textSizeOption ? textSizeOption.fontSize : '14px';
  
  const baseStyle = sizeMap[size] || sizeMap.medium;
  
  return {
    ...baseStyle,
    fontSize: fontSize
  };
};

/**
 * NodeEditMenu Component
 * 
 * A floating menu for editing node properties inline
 * Positioned dynamically relative to the node being edited
 * 
 * @param {Object} props - Component properties
 * @param {Object} props.node - The node being edited
 * @param {Function} props.onUpdateNode - Callback to update node data
 * @param {Function} props.onClose - Callback to close the menu
 * @param {Object} props.position - Menu position coordinates
 */
const NodeEditMenu = ({ node, onUpdateNode, onClose, position }) => {
  const [tempLabel, setTempLabel] = useState(node.data.label || '');
  const [tempColor, setTempColor] = useState(node.data.color || '#3b82f6');
  const [tempSize, setTempSize] = useState(node.data.size || 'medium');
  const [tempTextSize, setTempTextSize] = useState(node.data.textSize || 'base');
  const [tempNodeType, setTempNodeType] = useState(node.data.nodeType || 'default');

  const handleSave = useCallback(() => {
    onUpdateNode(node.id, {
      label: tempLabel,
      color: tempColor,
      size: tempSize,
      textSize: tempTextSize,
      nodeType: tempNodeType
    });
    onClose();
  }, [tempLabel, tempColor, tempSize, tempTextSize, tempNodeType, node.id, onUpdateNode, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Calculate responsive positioning
  const menuStyle = useMemo(() => ({
    left: position.x - 140,
    top: position.y - 50,
    transform: position.x > window.innerWidth - 200 ? 'translateX(-100%)' : 'none'
  }), [position]);

  return (
    <div 
      className="absolute z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 min-w-[280px]"
      style={menuStyle}
    >
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Edit Node</h3>
        <Button
          isIconOnly
          size="sm"
          variant="light"
          onClick={handleCancel}
          className="min-w-0 w-6 h-6"
          aria-label="Close menu"
        >
          ✕
        </Button>
      </div>

      <div className="space-y-3">
        {/* Label Input */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Label
          </label>
          <Input
            value={tempLabel}
            onChange={(e) => setTempLabel(e.target.value)}
            size="sm"
            className="w-full"
            placeholder="Enter node label"
          />
        </div>

        {/* Node Type Select */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Type
          </label>
          <select
            value={tempNodeType}
            onChange={(e) => setTempNodeType(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {Object.keys(NODE_TYPE_STYLES).map((type) => (
              <option key={type} value={type}>
                {NODE_TYPE_STYLES[type].icon} {type}
              </option>
            ))}
          </select>
        </div>

        {/* Color Select */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Color
          </label>
          <select
            value={tempColor}
            onChange={(e) => setTempColor(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {COLOR_OPTIONS.map((color) => (
              <option key={color.value} value={color.value}>
                {color.label}
              </option>
            ))}
          </select>
        </div>

        {/* Size Select */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Size
          </label>
          <select
            value={tempSize}
            onChange={(e) => setTempSize(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {SIZE_OPTIONS.map((size) => (
              <option key={size.value} value={size.value}>
                {size.label}
              </option>
            ))}
          </select>
        </div>

        {/* Text Size Select */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Text Size
          </label>
          <select
            value={tempTextSize}
            onChange={(e) => setTempTextSize(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {TEXT_SIZE_OPTIONS.map((textSize) => (
              <option key={textSize.value} value={textSize.value}>
                {textSize.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button
          onClick={handleSave}
          color="primary"
          size="sm"
          className="flex-1"
        >
          Save
        </Button>
        <Button
          onClick={handleCancel}
          color="default"
          variant="light"
          size="sm"
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

/**
 * NoteNode Component
 * 
 * A custom ReactFlow node for adding notes to the case diagram
 * Features inline editing and resizable interface
 * 
 * @param {Object} props - Standard ReactFlow NodeProps
 * @param {Object} props.data - Node data including text and size
 * @param {string} props.id - Unique node identifier
 */
const NoteNode = ({ data, id }) => {
  const [text, setText] = useState(data.text || '');
  const [isEditing, setIsEditing] = useState(data.isNew || false);
  const [size] = useState(data.size || 'medium');
  const [textSize] = useState(data.textSize || 'base');
  const sizeStyles = getSizeStyles(size, textSize);
  
  // Access ReactFlow instance for updating nodes
  const { setNodes } = useReactFlow();

  // Calculate dynamic size based on text content
  const calculateDimensions = useCallback((textContent) => {
    if (!textContent || textContent.trim() === '') {
      return { width: 120, height: 80 }; // Default empty note size
    }

    const fontSize = parseInt(sizeStyles.fontSize);
    const padding = 16; // 8px padding on each side
    const lineHeight = fontSize * 1.3;
    
    // Estimate characters per line based on font size
    const avgCharWidth = fontSize * 0.6;
    const maxCharsPerLine = Math.floor((200 - padding) / avgCharWidth); // Max width 200px
    
    // Calculate dimensions
    const lines = textContent.split('\n');
    let totalLines = 0;
    let maxLineLength = 0;
    
    lines.forEach(line => {
      if (line.length === 0) {
        totalLines += 1;
      } else {
        const wrappedLines = Math.ceil(line.length / maxCharsPerLine);
        totalLines += wrappedLines;
        maxLineLength = Math.max(maxLineLength, Math.min(line.length, maxCharsPerLine));
      }
    });
    
    const width = Math.max(80, Math.min(300, (maxLineLength * avgCharWidth) + padding));
    const height = Math.max(60, (totalLines * lineHeight) + padding);
    
    return { width: Math.round(width), height: Math.round(height) };
  }, [sizeStyles.fontSize]);

  // Update node dimensions when text changes
  const updateNodeDimensions = useCallback((textContent) => {
    const dimensions = calculateDimensions(textContent);
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              style: {
                ...node.style,
                width: dimensions.width,
                height: dimensions.height
              },
              data: {
                ...node.data,
                text: textContent,
                isNew: false
              }
            }
          : node
      )
    );
  }, [id, setNodes, calculateDimensions]);

  const handleClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      setIsEditing(false);
      updateNodeDimensions(text);
    }
  }, [text, updateNodeDimensions]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    updateNodeDimensions(text);
  }, [text, updateNodeDimensions]);

  // Handle real-time text changes during editing
  const handleTextChange = useCallback((e) => {
    const newText = e.target.value;
    setText(newText);
    
    // Optional: Update dimensions in real-time while typing (debounced)
    // For better performance, we could add a debounce here
  }, []);

  // Initialize proper dimensions on mount or when data changes
  useEffect(() => {
    if (text && text.trim() !== '') {
      const dimensions = calculateDimensions(text);
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id && (!node.style?.width || node.style.width === 80)
            ? {
                ...node,
                style: {
                  ...node.style,
                  width: dimensions.width,
                  height: dimensions.height
                }
              }
            : node
        )
      );
    }
  }, [text, id, setNodes, calculateDimensions]);

  return (
    <div
      className="relative bg-yellow-100 border-2 border-yellow-300 rounded-lg shadow-sm cursor-text overflow-hidden"
      onClick={handleClick}
      style={{
        width: '100%',
        height: '100%',
        minWidth: '80px',
        minHeight: '60px',
        maxWidth: '300px',
        padding: '8px',
        boxSizing: 'border-box'
      }}
    >


      {isEditing ? (
        <textarea
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className="w-full h-full bg-transparent resize-none outline-none text-gray-700 p-1"
          placeholder="Type your note..."
          autoFocus
          style={{ 
            fontSize: sizeStyles.fontSize,
            lineHeight: '1.3',
            wordWrap: 'break-word'
          }}
        />
      ) : (
        <div
          className={`text-gray-700 h-full p-1 ${text ? 'text-left' : 'flex items-center justify-center'}`}
          style={{ 
            fontSize: sizeStyles.fontSize,
            lineHeight: '1.3',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto'
          }}
        >
          {text || 'Click to add note...'}
        </div>
              )}
      </div>
    );
  };

/**
 * QuickAddMenu Component
 * 
 * A dropdown menu that appears on hover over the Add Node button
 * Allows quick creation of nodes by type without opening a modal
 * 
 * @param {Object} props - Component properties
 * @param {boolean} props.isVisible - Whether the menu is visible
 * @param {Function} props.onSelectType - Callback when a node type is selected
 * @param {Function} props.onSelectTypeByClick - Callback when a node type is selected for click-to-place
 * @param {Object} props.position - Menu position relative to button
 */
const QuickAddMenu = ({ isVisible, onSelectType, position }) => {
  const handleTypeSelect = useCallback((nodeType) => {
    onSelectType(nodeType);
  }, [onSelectType]);

  if (!isVisible) return null;

  return (
    <div 
      className="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[180px] max-h-[300px] overflow-y-auto"
      style={{
        left: position.x,
        top: position.y + 40,
      }}
    >
      <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">
        Click to Place Node
      </div>
      {Object.entries(NODE_TYPE_STYLES).map(([type, style]) => (
        <button
          key={type}
          onClick={() => handleTypeSelect(type)}
          className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none transition-colors flex items-center gap-2 text-sm"
        >
          <span className="text-base">{style.icon}</span>
          <span className="capitalize font-medium text-gray-700">{type}</span>
          <span className="ml-auto text-blue-600 text-xs">📍</span>
        </button>
      ))}
    </div>
  );
};

/**
 * CaseNode Component
 * 
 * Custom ReactFlow node for case elements (suspects, evidence, etc.)
 * Features hover interactions, inline editing, and connection handles
 * Now supports quick label editing with single click on label
 * 
 * @param {Object} props - Standard ReactFlow NodeProps
 * @param {Object} props.data - Node data including label, type, color, and size
 * @param {string} props.id - Unique node identifier
 */
const CaseNode = ({ data, id }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(data.label);
  const [isHovered, setIsHovered] = useState(false);
  const [hideTimeout, setHideTimeout] = useState(null);
  
  const { setNodes, setEdges } = useReactFlow();
  
  // Get node styling
  const nodeType = data.nodeType || 'default';
  const style = NODE_TYPE_STYLES[nodeType] || NODE_TYPE_STYLES.default;
  const nodeColor = data.color || style.border;
  const nodeSize = data.size || 'medium';
  const nodeTextSize = data.textSize || 'base';
  const sizeStyles = getSizeStyles(nodeSize, nodeTextSize);

  const handleDoubleClick = useCallback(() => {
    setIsEditing(true);
  }, []);

  // New: Handle single click on label for quick editing
  const handleLabelClick = useCallback((event) => {
    event.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleEditClick = useCallback((event) => {
    event.stopPropagation();
    // Emit custom event for edit menu
    const rect = event.currentTarget.closest('.react-flow__node').getBoundingClientRect();
    window.dispatchEvent(new CustomEvent('node-edit', {
      detail: {
        nodeId: id,
        position: {
          x: rect.left + rect.width / 2,
          y: rect.top
        }
      }
    }));
  }, [id]);

  const handleDeleteClick = useCallback((event) => {
    event.stopPropagation();
    // Remove node and connected edges
    setNodes((nds) => nds.filter(n => n.id !== id));
    setEdges((eds) => eds.filter(e => e.source !== id && e.target !== id));
  }, [id, setNodes, setEdges]);

  const handleLabelChange = useCallback((event) => {
    setLabel(event.target.value);
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      setIsEditing(false);
      // Update node data
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  label: label
                }
              }
            : node
        )
      );
    }
    if (event.key === 'Escape') {
      setIsEditing(false);
      setLabel(data.label); // Reset to original label
    }
  }, [label, id, setNodes, data.label]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    // Update node data
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                label: label
              }
            }
          : node
      )
    );
  }, [label, id, setNodes]);

  const handleMouseEnter = useCallback(() => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      setHideTimeout(null);
    }
    setIsHovered(true);
  }, [hideTimeout]);

  const handleMouseLeave = useCallback(() => {
    const timeout = setTimeout(() => {
      setIsHovered(false);
    }, 200);
    setHideTimeout(timeout);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [hideTimeout]);

  return (
    <div 
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* ReactFlow connection handles */}
      <Handle 
        type="target" 
        position={Position.Top} 
        className="w-2 h-2 bg-blue-500" 
      />
      
      {/* Hover Actions */}
      {isHovered && !isEditing && (
        <div className="absolute -top-10 -right-2 flex gap-1 z-20 p-1">
          <Button
            isIconOnly
            size="sm"
            variant="solid"
            color="primary"
            onClick={handleEditClick}
            className="min-w-0 w-7 h-7 text-xs hover:scale-110 transition-transform"
            aria-label="Edit node"
          >
            ✏️
          </Button>
          <Button
            isIconOnly
            size="sm"
            variant="solid"
            color="danger"
            onClick={handleDeleteClick}
            className="min-w-0 w-7 h-7 text-xs hover:scale-110 transition-transform"
            aria-label="Delete node"
          >
            🗑️
          </Button>
        </div>
      )}

      {/* Node Type Label */}
      <div 
        className="absolute -top-2 left-1/2 transform -translate-x-1/2 px-2 py-1 rounded-md text-xs font-medium bg-white border border-gray-300 shadow-sm"
        style={{ 
          fontSize: '9px',
          color: '#6b7280',
          zIndex: 10
        }}
      >
        {nodeType}
      </div>
      
      {/* Main Node */}
      <div 
        onDoubleClick={handleDoubleClick} 
        className="shadow-lg rounded-lg border-2 text-center bg-white hover:shadow-xl transition-shadow"
        style={{ 
          borderColor: nodeColor,
          backgroundColor: data.backgroundColor || '#ffffff',
          width: '100%',
          height: '100%',
          padding: sizeStyles.padding,
          minWidth: sizeStyles.width,
          minHeight: sizeStyles.height,
          boxSizing: 'border-box'
        }}
      >
        {isEditing ? (
          <input
            type="text"
            value={label}
            onChange={handleLabelChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className="bg-transparent text-center w-full outline-none text-gray-700"
            style={{ fontSize: sizeStyles.fontSize }}
            autoFocus
            placeholder="Enter label..."
          />
        ) : (
          <div 
            className="font-medium text-gray-700 flex items-center justify-center h-full cursor-pointer hover:bg-gray-50 rounded transition-colors"
            style={{ fontSize: sizeStyles.fontSize }}
            onClick={handleLabelClick}
            title="Click to edit label"
          >
            {label}
          </div>
        )}
      </div>
      
      <Handle 
        type="source" 
        position={Position.Bottom} 
        className="w-2 h-2 bg-green-500" 
      />
    </div>
  );
};

/**
 * Node type mapping for ReactFlow
 * Maps string identifiers to React components
 */
const nodeTypes = {
  caseNode: CaseNode,
  note: NoteNode,
};

/**
 * Layout algorithm to organize nodes in a structured way
 * Groups nodes by type and positions them in designated areas
 * 
 * @param {Array} nodes - Array of ReactFlow nodes
 * @returns {Array} Array of nodes with updated positions
 */
const organizeNodes = (nodes) => {
  if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
    console.warn('organizeNodes called with invalid nodes:', nodes);
    return [];
  }
  
  const nodesByType = {};
  
  // Group nodes by type
  nodes.forEach(node => {
    const type = node.data.nodeType || 'default';
    if (!nodesByType[type]) {
      nodesByType[type] = [];
    }
    nodesByType[type].push(node);
  });

  // Define layout areas for different node types
  const typePositions = {
    case: { x: 400, y: 50, spacing: 150 },
    officer: { x: 100, y: 200, spacing: 120 },
    suspect: { x: 700, y: 200, spacing: 120 },
    victim: { x: 100, y: 350, spacing: 120 },
    witness: { x: 400, y: 350, spacing: 120 },
    location: { x: 700, y: 350, spacing: 120 },
    evidence: { x: 250, y: 500, spacing: 120 },
    event: { x: 550, y: 500, spacing: 120 },
    person: { x: 400, y: 200, spacing: 120 },
    default: { x: 400, y: 650, spacing: 120 }
  };

  let organizedNodes = [];
  
  // Position nodes by type
  Object.entries(nodesByType).forEach(([type, typeNodes]) => {
    const basePos = typePositions[type] || typePositions.default;
    
    typeNodes.forEach((node, index) => {
      const row = Math.floor(index / 3);
      const col = index % 3;
      
      organizedNodes.push({
        ...node,
        position: {
          x: basePos.x + (col * basePos.spacing) - (basePos.spacing * Math.min(typeNodes.length - 1, 2) / 2),
          y: basePos.y + (row * 100)
        }
      });
    });
  });

  return organizedNodes;
};

/**
 * Main CaseMapper Component
 * 
 * A comprehensive ReactFlow-based case mapping application
 * Integrates all subcomponents and provides the main interface
 */
const CaseMapper = () => {
  // ReactFlow state management hooks
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [rfInstance, setRfInstance] = useState(null);
  
  // Component state
  const [reportText, setReportText] = useState('');
  const [isMapping, setIsMapping] = useState(false);
  const [error, setError] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [editMenuPosition, setEditMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isAddingNode, setIsAddingNode] = useState(false);
  const [pendingNodeType, setPendingNodeType] = useState('default');
  
  // Quick add menu state
  const [isQuickAddVisible, setIsQuickAddVisible] = useState(false);
  const [quickAddPosition, setQuickAddPosition] = useState({ x: 0, y: 0 });
  const [quickAddTimeout, setQuickAddTimeout] = useState(null);
  
  // History management
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Modal states
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const { 
    isOpen: isAddNodeOpen, 
    onOpen: onAddNodeOpen, 
    onOpenChange: onAddNodeOpenChange 
  } = useDisclosure();
  const { 
    isOpen: isSaveAsOpen, 
    onOpen: onSaveAsOpen, 
    onOpenChange: onSaveAsOpenChange 
  } = useDisclosure();
  
  // Add Node form state
  const [newNodeLabel, setNewNodeLabel] = useState('');
  const [newNodeType, setNewNodeType] = useState('default');
  const [newNodeColor, setNewNodeColor] = useState('#3b82f6');
  const [newNodeSize, setNewNodeSize] = useState('medium');
  const [newNodeTextSize, setNewNodeTextSize] = useState('base');
  const [saveFileName, setSaveFileName] = useState(`case-map-${new Date().toISOString().split('T')[0]}.json`);

  // File input ref for import functionality
  const fileInputRef = React.useRef(null);
  const addNodeButtonRef = React.useRef(null);

  // Get ReactFlow instance methods
  const { fitView, screenToFlowPosition, getViewport } = useReactFlow();
  
  /**
   * Clear all connecting lines (edges) from the flow
   */
  const onClearEdges = useCallback(() => {
    setEdges([]);
  }, [setEdges]);

  /**
   * Save current state to history for undo functionality
   */
  const saveToHistory = useCallback(() => {
    const currentState = {
      nodes: nodes,
      edges: edges,
      timestamp: Date.now()
    };
    
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(currentState);
      return newHistory.slice(-MAX_HISTORY);
    });
    
    setHistoryIndex(prev => prev + 1);
  }, [nodes, edges, historyIndex, setHistory, setHistoryIndex]);

  /**
   * Undo functionality - restore previous state
   */
  const onUndo = useCallback(() => {
    if (historyIndex > 0 && history.length > 0) {
      const previousState = history[historyIndex - 1];
      
      if (previousState && previousState.nodes && previousState.edges) {
        setNodes(previousState.nodes);
        setEdges(previousState.edges);
        setHistoryIndex(prev => prev - 1);
      }
    }
  }, [history, historyIndex, setNodes, setEdges]);

  /**
   * Handle edge connections between nodes
   */
  const onConnect = useCallback((params) => {
    const edge = {
      ...params,
      animated: true,
      style: { stroke: '#3b82f6', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed }
    };
    setEdges((eds) => addEdge(edge, eds));
  }, [setEdges]);

  /**
   * Update node properties
   */
  const onUpdateNode = useCallback((nodeId, updates) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                ...updates
              }
            }
          : node
      )
    );
    setEditingNode(null);
  }, [setNodes]);

  /**
   * Handle node edit menu events
   */
  useEffect(() => {
    const handleNodeEdit = (event) => {
      const { nodeId, position } = event.detail;
      const node = nodes.find(n => n.id === nodeId);
      if (node) {
        setEditingNode(node);
        setEditMenuPosition(position);
      }
    };

    window.addEventListener('node-edit', handleNodeEdit);
    return () => window.removeEventListener('node-edit', handleNodeEdit);
  }, [nodes]);

  /**
   * Close edit menu
   */
  const onCloseEditMenu = useCallback(() => {
    setEditingNode(null);
  }, []);

  /**
   * Add new node to the flow
   */
  const onAddNode = useCallback(() => {
    setNewNodeLabel('');
    setNewNodeType('default');
    setNewNodeColor('#3b82f6');
    setNewNodeSize('medium');
    setNewNodeTextSize('base');
    onAddNodeOpen();
  }, [onAddNodeOpen, setNewNodeLabel, setNewNodeType, setNewNodeColor, setNewNodeSize, setNewNodeTextSize]);

  /**
   * Create new node with form data
   */
  const onCreateNode = useCallback(() => {
    const sizeStyles = getSizeStyles(newNodeSize, newNodeTextSize);
    const newNode = {
      id: `${nodeIdCounter++}`,
      type: 'caseNode',
      data: { 
        label: newNodeLabel || `Node ${nodeIdCounter - 1}`, 
        nodeType: newNodeType,
        color: newNodeColor,
        size: newNodeSize,
        textSize: newNodeTextSize
      },
      position: {
        x: Math.random() * 400,
        y: Math.random() * 400,
      },
      style: {
        width: parseInt(sizeStyles.width),
        height: parseInt(sizeStyles.height)
      }
    };
    setNodes((nds) => nds.concat(newNode));
    onAddNodeOpenChange(false);
  }, [newNodeLabel, newNodeType, newNodeColor, newNodeSize, newNodeTextSize, setNodes, onAddNodeOpenChange]);

  /**
   * Handle edge click for deletion
   */
  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    setEdges((eds) => eds.filter(e => e.id !== edge.id));
  }, [setEdges]);

  /**
   * Handle nodes deletion and cleanup connected edges
   */
  const onNodesDelete = useCallback((deleted) => {
    setEdges(
      deleted.reduce((acc, node) => {
        const connectedEdges = getConnectedEdges([node], edges);
        return acc.filter((edge) => !connectedEdges.includes(edge));
      }, edges)
    );
  }, [edges, setEdges]);


  /**
   * Restore flow from localStorage
   */
  const onRestore = useCallback(() => {
    const restoreFlow = async () => {
      const flow = JSON.parse(localStorage.getItem(FLOW_KEY) || '{}');
      if (flow.nodes && flow.edges) {
        setNodes(flow.nodes);
        setEdges(flow.edges);
      }
    };
    restoreFlow();
  }, [setNodes, setEdges]);

  /**
   * AI-powered case mapping from text report
   */
  const onMapWithAI = useCallback(async () => {
    if (!reportText.trim()) {
      setError('Please enter a report text to map');
      return;
    }

    setIsMapping(true);
    setError(null);

    try {
      const result = await getAIMappedCase(reportText);
      
      if (result.error) {
        setError(result.error);
        return;
      }

      // Process AI results with enhanced edges
      const updatedEdges = (result.edges || []).map(edge => ({
        ...edge,
        style: { stroke: '#3b82f6', strokeWidth: 2 },
        animated: true,
        markerEnd: { type: MarkerType.ArrowClosed }
      }));

      // Enhance nodes with default properties
      const enhancedNodes = (result.nodes || []).map(node => {
        const nodeSize = node.data.size || 'medium';
        const nodeTextSize = node.data.textSize || 'base';
        const sizeStyles = getSizeStyles(nodeSize, nodeTextSize);
        return {
          ...node,
          type: 'caseNode', // Ensure custom node type
          data: {
            ...node.data,
            color: node.data.color || '#3b82f6',
            size: nodeSize,
            textSize: nodeTextSize
          },
          style: {
            width: parseInt(sizeStyles.width),
            height: parseInt(sizeStyles.height),
            ...node.style
          }
        };
      });

      // Apply auto-layout
      const organizedNodes = organizeNodes(enhancedNodes);
      
      setNodes(organizedNodes);
      setEdges(updatedEdges);
      
      // Update node counter
      const maxId = Math.max(...(enhancedNodes || []).map(node => parseInt(node.id) || 0));
      nodeIdCounter = maxId + 1;
      
      // Close modal and fit view
      onOpenChange(false);
      setTimeout(() => fitView(), 100); // Delay to ensure nodes are rendered
      
    } catch (err) {
      setError(err.message || 'Failed to generate case map');
    } finally {
      setIsMapping(false);
    }
  }, [reportText, setNodes, setEdges, onOpenChange, fitView]);

  /**
   * Organize existing nodes using layout algorithm
   */
  const onOrganizeNodes = useCallback(() => {
    if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
      return;
    }
    
    const organizedNodes = organizeNodes(nodes);
    setNodes(organizedNodes);
  }, [nodes, setNodes]);

  /**
   * Handle canvas click for note placement, node placement, or menu dismissal
   */
  const handleCanvasClick = useCallback((event) => {
    if (isAddingNote) {
      // Use ReactFlow's screenToFlowPosition to get accurate coordinates
      let position;
      
      if (screenToFlowPosition) {
        position = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });
        // Small offset to center note on cursor
        position.x -= 40;
        position.y -= 30;
      } else {
        // Fallback method
        const rect = event.currentTarget.getBoundingClientRect();
        position = {
          x: event.clientX - rect.left - 40,
          y: event.clientY - rect.top - 30
        };
      }

      // Create new note with default dynamic size
      const newNote = {
        id: `note-${nodeIdCounter++}`,
        type: 'note',
        position: position,
        data: { 
          text: '', 
          isNew: true,
          size: 'medium',
          textSize: 'base'
        },
        style: {
          width: 120,
          height: 80
        }
      };

      setNodes((nds) => nds.concat(newNote));
      setIsAddingNote(false);
    } else if (isAddingNode) {
      // Use ReactFlow's screenToFlowPosition to get accurate coordinates
      let position;
      
      if (screenToFlowPosition) {
        position = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });
        // Small offset to center node on cursor
        position.x -= 60;
        position.y -= 40;
      } else {
        // Fallback method
        const rect = event.currentTarget.getBoundingClientRect();
        position = {
          x: event.clientX - rect.left - 60,
          y: event.clientY - rect.top - 40
        };
      }

      // Create a more descriptive default label based on node type
      const defaultLabels = {
        suspect: 'Suspect',
        victim: 'Victim', 
        witness: 'Witness',
        officer: 'Officer',
        location: 'Location',
        evidence: 'Evidence',
        event: 'Event',
        person: 'Person',
        case: 'Case',
        default: 'Node'
      };

      const sizeStyles = getSizeStyles('medium', 'base');
      const newNode = {
        id: `${nodeIdCounter++}`,
        type: 'caseNode',
        position: position,
        data: { 
          label: defaultLabels[pendingNodeType] || 'Node',
          nodeType: pendingNodeType,
          color: NODE_TYPE_STYLES[pendingNodeType]?.border || '#3b82f6',
          size: 'medium',
          textSize: 'base'
        },
        style: {
          width: parseInt(sizeStyles.width),
          height: parseInt(sizeStyles.height)
        }
      };

      setNodes((nds) => nds.concat(newNode));
      setIsAddingNode(false);
    } else {
      setEditingNode(null);
    }
  }, [isAddingNote, isAddingNode, pendingNodeType, setNodes, screenToFlowPosition]);

  /**
   * Toggle note adding mode
   */
  const onAddNote = useCallback(() => {
    setIsAddingNote(!isAddingNote);
    // Disable node adding mode if it's active
    if (isAddingNode) {
      setIsAddingNode(false);
    }
  }, [isAddingNote, isAddingNode]);

  /**
   * Toggle node adding mode
   */
  const onAddNodeByClick = useCallback((nodeType = 'default') => {
    setPendingNodeType(nodeType);
    setIsAddingNode(true);
    // Disable note adding mode if it's active
    if (isAddingNote) {
      setIsAddingNote(false);
    }
  }, [isAddingNote]);

  /**
   * Delete selected nodes
   */
  const onDeleteSelected = useCallback(() => {
    if (selectedNodes.length > 0) {
      const selectedNodeIds = selectedNodes.map(node => node.id);
      setNodes((nds) => nds.filter(node => !selectedNodeIds.includes(node.id)));
      setEdges((eds) => eds.filter(edge => 
        !selectedNodeIds.includes(edge.source) && !selectedNodeIds.includes(edge.target)
      ));
      setSelectedNodes([]);
    }
  }, [selectedNodes, setNodes, setEdges]);

  /**
   * Handle selection changes
   */
  const onSelectionChange = useCallback((params) => {
    setSelectedNodes(params.nodes || []);
  }, []);

  /**
   * Handle ESC key to cancel note mode or node mode
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (isAddingNote) {
          setIsAddingNote(false);
        }
        if (isAddingNode) {
          setIsAddingNode(false);
        }
      }
    };

    if (isAddingNote || isAddingNode) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isAddingNote, isAddingNode]);

  /**
   * Track changes for history
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveToHistory();
    }, HISTORY_SAVE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [nodes, edges, saveToHistory]);

  /**
   * Open Save As dialog
   */
  const onExportToFile = useCallback(() => {
    setSaveFileName(`case-map-${new Date().toISOString().split('T')[0]}.json`);
    onSaveAsOpen();
  }, [onSaveAsOpen]);

  /**
   * Perform the actual export with a given filename
   */
  const performExportToFile = useCallback((fileName) => {
    const exportData = {
      nodes: nodes,
      edges: edges,
      nodeIdCounter: nodeIdCounter,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [nodes, edges]);

  /**
   * Import case map from JSON file
   */
  const onImportFromFile = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setError('Please select a valid JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        
        // Validate imported data structure
        if (!importData.nodes || !Array.isArray(importData.nodes)) {
          throw new Error('Invalid file format: missing nodes array');
        }
        if (!importData.edges || !Array.isArray(importData.edges)) {
          throw new Error('Invalid file format: missing edges array');
        }

        // Restore node ID counter
        if (importData.nodeIdCounter) {
          nodeIdCounter = Math.max(nodeIdCounter, importData.nodeIdCounter);
        }

        // Restore state
        setNodes(importData.nodes);
        setEdges(importData.edges);
        setError(null);
        
        // Fit view after import
        setTimeout(() => fitView(), 100);
        
      } catch (err) {
        console.error('Import error:', err);
        setError(`Failed to import file: ${err.message}`);
      }
    };
    
    reader.onerror = () => {
      setError('Failed to read file');
    };
    
    reader.readAsText(file);
    
    // Clear the input so the same file can be selected again
    event.target.value = '';
  }, [setNodes, setEdges, fitView]);

  /**
   * Trigger file import dialog
   */
  const onImportFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);


  /**
   * Handle quick node type selection for click-to-place mode
   */
  const onQuickAddNodeByClick = useCallback((nodeType) => {
    onAddNodeByClick(nodeType);
    setIsQuickAddVisible(false);
  }, [onAddNodeByClick]);

  /**
   * Handle Add Node button hover
   */
  const handleAddNodeHover = useCallback((event) => {
    if (quickAddTimeout) {
      clearTimeout(quickAddTimeout);
      setQuickAddTimeout(null);
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    setQuickAddPosition({
      x: rect.left,
      y: rect.top
    });
    setIsQuickAddVisible(true);
  }, [quickAddTimeout]);

  /**
   * Handle Add Node button mouse leave
   */
  const handleAddNodeLeave = useCallback(() => {
    const timeout = setTimeout(() => {
      setIsQuickAddVisible(false);
    }, 300);
    setQuickAddTimeout(timeout);
  }, []);

  /**
   * Handle quick add menu mouse enter (prevent hiding)
   */
  const handleQuickAddEnter = useCallback(() => {
    if (quickAddTimeout) {
      clearTimeout(quickAddTimeout);
      setQuickAddTimeout(null);
    }
  }, [quickAddTimeout]);

  /**
   * Handle quick add menu mouse leave
   */
  const handleQuickAddLeave = useCallback(() => {
    const timeout = setTimeout(() => {
      setIsQuickAddVisible(false);
    }, 100);
    setQuickAddTimeout(timeout);
  }, []);

  // Cleanup quick add timeout on unmount
  useEffect(() => {
    return () => {
      if (quickAddTimeout) {
        clearTimeout(quickAddTimeout);
      }
    };
  }, [quickAddTimeout]);

  /**
   * Handle printing the canvas using html-to-image library
   */
  const onPrintCanvas = useCallback(async () => {
    try {
      // Temporarily fit view to ensure all nodes are visible for capture
      const originalViewport = getViewport();
      fitView();

      // Wait for a moment to ensure rendering is complete after fitView
      await new Promise(resolve => setTimeout(resolve, 200));

      // Get the ReactFlow wrapper element
      const reactFlowWrapper = document.querySelector('.react-flow');
      
      if (!reactFlowWrapper) {
        console.error('Could not find ReactFlow wrapper element for printing.');
        alert('Could not capture the flow for printing. Please try again.');
        return;
      }

      // Dynamic import of html-to-image to avoid bundling if not needed
      const { toPng } = await import('html-to-image');

      const dataUrl = await toPng(reactFlowWrapper, {
        backgroundColor: '#ffffff',
        width: reactFlowWrapper.offsetWidth,
        height: reactFlowWrapper.offsetHeight,
        style: {
          width: reactFlowWrapper.offsetWidth + 'px',
          height: reactFlowWrapper.offsetHeight + 'px',
        }
      });

      // Restore original viewport
      if (rfInstance) {
        rfInstance.setViewport(originalViewport);
      }

      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Case Map Print</title>
              <style>
                body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
                img { max-width: 100%; max-height: 100%; display: block; }
              </style>
            </head>
            <body>
              <img src="${dataUrl}" onload="window.print(); window.close();" />
            </body>
          </html>
        `);
        printWindow.document.close();
      } else {
        alert('Please allow pop-ups for printing.');
      }
    } catch (error) {
      console.error('Error printing canvas:', error);
      alert('Failed to print canvas. Please try again.');
    }
  }, [fitView, getViewport, rfInstance]);

  return (
    <div className="w-full h-full relative">
      {/* Edit Menu */}
      {editingNode && (
        <NodeEditMenu
          key={editingNode.id}
          node={editingNode}
          onUpdateNode={onUpdateNode}
          onClose={onCloseEditMenu}
          position={editMenuPosition}
        />
      )}

      {/* Quick Add Menu */}
      <div
        onMouseEnter={handleQuickAddEnter}
        onMouseLeave={handleQuickAddLeave}
      >
        <QuickAddMenu
          isVisible={isQuickAddVisible}
          onSelectType={onQuickAddNodeByClick}
          position={quickAddPosition}
        />
      </div>

      {/* Floating Control Panel */}
      <div className="absolute top-4 left-4 z-10 flex gap-2">
        <Button
          onClick={onOpen}
          color="secondary"
          variant="shadow"
          size="lg"
        >
          📝 Import Report
        </Button>
        <Button
          onClick={onUndo}
          color="warning"
          variant="flat"
          size="sm"
          isDisabled={historyIndex <= 0 || history.length === 0}
        >
          ↶ Undo
        </Button>
        <Button
          onClick={onOrganizeNodes}
          color="primary"
          variant="flat"
          size="sm"
          isDisabled={!nodes || !Array.isArray(nodes) || nodes.length === 0}
        >
          🎯 Organize
        </Button>
        <Button
          onClick={onPrintCanvas}
          color="info"
          variant="flat"
          size="sm"
          isDisabled={!nodes || !Array.isArray(nodes) || nodes.length === 0}
        >
          🖨️ Print
        </Button>
        <Button
          onClick={onClearEdges}
          color="danger"
          variant="flat"
          size="sm"
          isDisabled={edges.length === 0}
        >
          Clear Lines
        </Button>
        <Button
          onClick={onImportFile}
          color="warning"
          variant="flat"
          size="sm"
        >
          📂 Open
        </Button>
        <Button
          onClick={onExportToFile}
          color="success"
          variant="flat"
          size="sm"
          isDisabled={!nodes || !Array.isArray(nodes) || nodes.length <= 1}
        >
          💾 Save
        </Button>
        <Button
          onClick={onRestore}
          color="warning"
          variant="flat"
          size="sm"
        >
          Restore
        </Button>
        <div className="relative">
          <Button
            ref={addNodeButtonRef}
            onClick={onAddNode}
            onMouseEnter={handleAddNodeHover}
            onMouseLeave={handleAddNodeLeave}
            color="primary"
            variant="flat"
            size="sm"
          >
            Add Node
          </Button>
        </div>
        <Button
          onClick={onAddNote}
          color={isAddingNote ? "success" : "secondary"}
          variant={isAddingNote ? "solid" : "flat"}
          size="sm"
        >
          {isAddingNote ? "📝 Click to Place" : "📝 Add Note"}
        </Button>
        {selectedNodes.length > 0 && (
          <Button
            onClick={onDeleteSelected}
            color="danger"
            variant="flat"
            size="sm"
          >
            Delete Selected ({selectedNodes.length})
          </Button>
        )}
      </div>

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        onChange={onImportFromFile}
        style={{ display: 'none' }}
        aria-label="Import case map file"
      />

      {/* Note adding instruction */}
      {isAddingNote && (
        <div className="absolute top-20 left-4 z-10 bg-green-100 border border-green-300 p-3 rounded-lg shadow-lg">
          <p className="text-sm text-green-700 font-medium">📝 Note Mode Active</p>
          <p className="text-xs text-green-600 mt-1">Click anywhere on the canvas to place a note</p>
          <p className="text-xs text-green-600">Press ESC or click the button again to cancel</p>
        </div>
      )}

      {/* Node adding instruction */}
      {isAddingNode && (
        <div className="absolute top-20 left-4 z-10 bg-blue-100 border border-blue-300 p-3 rounded-lg shadow-lg">
          <p className="text-sm text-blue-700 font-medium">🎯 Node Mode Active</p>
          <p className="text-xs text-blue-600 mt-1">Click anywhere on the canvas to place a {pendingNodeType} node</p>
          <p className="text-xs text-blue-600">Press ESC to cancel</p>
        </div>
      )}

      {/* Quick label editing instruction */}
      {!isAddingNote && !isAddingNode && (
        <div className="absolute bottom-4 left-4 z-10 bg-blue-50 border border-blue-200 p-3 rounded-lg shadow-lg max-w-sm">
          <p className="text-xs text-blue-700 font-medium">💡 Quick Tips</p>
          <p className="text-xs text-blue-600 mt-1">• Click on any node label to edit it quickly</p>
          <p className="text-xs text-blue-600">• Hover over "Add Node" for quick type selection</p>
          <p className="text-xs text-blue-600">• Select node type from "Add Node" menu to place by clicking</p>
          <p className="text-xs text-blue-600">• Press Enter to save, Escape to cancel</p>
        </div>
      )}

      {/* Selection Info */}
      {selectedNodes.length > 0 && (
        <div className="absolute top-4 right-4 z-10 bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="text-sm text-gray-600">
            {selectedNodes.length} node{selectedNodes.length > 1 ? 's' : ''} selected
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Hold Ctrl/Cmd to select multiple nodes
          </p>
        </div>
      )}

      {/* Report Input Modal */}
      <Modal 
        isOpen={isOpen} 
        onOpenChange={onOpenChange}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold">Import Case Report</h3>
                <p className="text-sm text-gray-600">Paste your police report or case statement to generate a visual case map</p>
              </ModalHeader>
              <ModalBody>
                <Textarea
                  value={reportText}
                  onChange={(e) => setReportText(e.target.value)}
                  placeholder="Paste your police report or case statement here..."
                  minRows={8}
                  maxRows={12}
                  className="w-full"
                />
                {error && (
                  <Chip color="danger" variant="flat" size="sm">
                    {error}
                  </Chip>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={onMapWithAI}
                  isDisabled={isMapping || !reportText.trim()}
                  color="secondary"
                  isLoading={isMapping}
                >
                  {isMapping ? 'Mapping...' : 'Generate Case Map'}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Add Node Modal */}
      <Modal 
        isOpen={isAddNodeOpen} 
        onOpenChange={onAddNodeOpenChange}
        size="md"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold">Add New Node</h3>
                <p className="text-sm text-gray-600">Configure your new node properties</p>
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Label</label>
                    <Input
                      value={newNodeLabel}
                      onChange={(e) => setNewNodeLabel(e.target.value)}
                      placeholder="Enter custom label or leave empty for default"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                    <select
                      value={newNodeType}
                      onChange={(e) => setNewNodeType(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {Object.keys(NODE_TYPE_STYLES).map((type) => (
                        <option key={type} value={type}>
                          {NODE_TYPE_STYLES[type].icon} {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                    <select
                      value={newNodeColor}
                      onChange={(e) => setNewNodeColor(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {COLOR_OPTIONS.map((color) => (
                        <option key={color.value} value={color.value}>
                          {color.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Size</label>
                    <select
                      value={newNodeSize}
                      onChange={(e) => setNewNodeSize(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {SIZE_OPTIONS.map((size) => (
                        <option key={size.value} value={size.value}>
                          {size.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Text Size</label>
                    <select
                      value={newNodeTextSize}
                      onChange={(e) => setNewNodeTextSize(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {TEXT_SIZE_OPTIONS.map((textSize) => (
                        <option key={textSize.value} value={textSize.value}>
                          {textSize.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={onCreateNode}
                  color="primary"
                >
                  Create Node
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Save As Modal */}
      <Modal 
        isOpen={isSaveAsOpen} 
        onOpenChange={onSaveAsOpenChange}
        size="md"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <h3 className="text-lg font-semibold">Save Case Map As</h3>
                <p className="text-sm text-gray-600">Enter a filename for your case map (e.g., my-case-map.json)</p>
              </ModalHeader>
              <ModalBody>
                <Input
                  value={saveFileName}
                  onChange={(e) => setSaveFileName(e.target.value)}
                  placeholder="Enter filename"
                  className="w-full"
                />
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    performExportToFile(saveFileName);
                    onClose();
                  }}
                  color="primary"
                  isDisabled={!saveFileName.trim()}
                >
                  Save
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* ReactFlow Canvas */}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodesDelete={onNodesDelete}
        onConnect={onConnect}
        onInit={setRfInstance}
        onPaneClick={handleCanvasClick}
        onEdgeClick={onEdgeClick}
        onSelectionChange={onSelectionChange}
        nodeTypes={nodeTypes}
        fitView
        className={`bg-gray-100 dark:bg-gray-900 ${isAddingNote || isAddingNode ? 'cursor-crosshair' : ''}`}
        defaultEdgeOptions={{
          style: { stroke: '#3b82f6', strokeWidth: 2 },
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed }
        }}
        connectionLineType={ConnectionLineType.SmoothStep}
        multiSelectionKeyCode="Control"
        selectionKeyCode="Control"
        panOnDrag={[1, 2]}
        selectNodesOnDrag={false}
        snapToGrid={true}
        snapGrid={SNAP_GRID}
        defaultViewport={DEFAULT_VIEWPORT}
      >
        <Controls />
        <Background />
      </ReactFlow>
    </div>
  );
};

export default CaseMapper;
